/**
 * Контекстное меню ПРОСТРАНСТВА (правый клик по иконке в рельсе).
 *
 * Отдельный компонент, а не ветка внутри RoomMenu: RoomMenu оперирует
 * RoomSummary (строкой списка чатов), а пространство в списке чатов не
 * появляется вовсе — его единственная «строка» это круглая иконка рельсы.
 * Натягивать RoomSummary на спейс означало бы выдумывать поля, которых
 * у него нет (previewText, notifying и т.д.).
 *
 * Пункты: пригласить человека в спейс, переименовать, выйти.
 * Создания спейса здесь НЕТ — оно на кнопке «＋» внизу рельсы (рядом с
 * иконками), а не в меню конкретного пространства.
 *
 * Механика закрытия и позиционирования повторяет RoomMenu ОДИН В ОДИН:
 * меню, закрывающееся непривычно, воспринимается как зависшее; а подписка
 * на window откладывается на тик по той же причине — иначе открывающее
 * событие contextmenu тут же закроет меню обратно (см. комментарий в
 * RoomMenu, дефект найден живым прогоном).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useT } from "../i18n";

import type { SpaceInfo } from "../matrix/rooms";
import { s } from "./styles";
import { color } from "./theme";

export interface SpaceMenuTarget {
  space: SpaceInfo;
  x: number;
  y: number;
}

export function SpaceMenu({
  target,
  onClose,
  onInvite: _onInvite,
  onRename,
  onMembers: _onMembers,
  onLeave: _onLeave,
}: {
  target: SpaceMenuTarget;
  onClose: () => void;
  /** Не передан = нет прав: пункт не рисуем (Антон 11:49 «admins only»). */
  onInvite?: (space: SpaceInfo) => void;
  onRename?: (space: SpaceInfo) => void;
  /** Участники пространства + удаление (Антон 11:56). Доступен всем: список
      видеть вправе любой участник, кнопка «Удалить» внутри уже по правам. */
  onMembers?: (space: SpaceInfo) => void;
  onLeave: (space: SpaceInfo) => void;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: target.x, top: target.y });
  const { space } = target;

  // См. комментарий-шапку: механика как у RoomMenu, включая отложенную
  // подписку на следующий тик.
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const id = setTimeout(() => {
      window.addEventListener("mousedown", close);
      window.addEventListener("contextmenu", close);
      window.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("mousedown", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(target.x, window.innerWidth - r.width - 8);
    const top = Math.min(target.y, window.innerHeight - r.height - 8);
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [target.x, target.y]);

  const item = (label: string, testId: string, fn: () => void, danger = false) => (
    <button
      type="button"
      style={{ ...s.menuItem, color: danger ? color.danger : color.text }}
      data-testid={testId}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => {
        fn();
        onClose();
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      ref={ref}
      style={{ ...s.menu, left: pos.left, top: pos.top }}
      data-testid="space-menu"
      data-space-id={space.roomId}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* ТОЛЬКО ДВА ПУНКТА (Антон 2026-09-12 12:55, по скриншоту меню:
          «тут должны остаться настройки и выйти»).

          Было четыре: пригласить, участники, настройки, выйти. Приглашение и
          участники — это управление составом, ему место внутри настроек, а не
          в быстром меню рельсы. В меню остаются вход в настройки и выход.

          onInvite/onMembers СОХРАНЕНЫ в интерфейсе компонента намеренно:
          вызывающая сторона (RoomList) и сами диалоги остаются рабочими,
          меняется только точка входа. Удалять их проводку раньше, чем появится
          новая точка, значило бы потерять функциональность совсем.

          ДОПОЛНЕНИЕ (Антон 12:56): «и выйти надо в настройки» — то есть в
          меню остаётся РОВНО ОДИН пункт — вход в настройки. Выход теперь
          последней строкой внутри настроек (RoomDialogs -> SpaceSettingsWithMembers).

          Пункт показывается ВСЕГДА, а не только админам. Раньше «Настройки»
          были admins-only (Антон 11:49), но теперь внутри лежат участники и
          выход — действия любого участника. Скрыть пункт у не-админа значило
          бы отнять у него возможность выйти из пространства вообще.
          Редактирование полей внутри и так закрыто правами: RoomSettings
          показывает «У вас нет прав на изменение настроек» и прячет «Изменить». */}
      {onRename && item(t("space.settingsMenu"), "space-menu-edit", () => onRename(space))}
    </div>
  );
}
